import { useState, useEffect } from 'react';
import { Pencil, Trash, DotsThree } from '@phosphor-icons/react';
import { adminFunctions } from '../../firebase/adminFunctions';
import {
  Table,
  Tbody,
  Thead,
  Tr,
  Th,
  Td,
  Button,
  Text,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Badge,
  IconButton,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  Textarea,
  Select,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';

export default function SiteManager() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSite, setEditingSite] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      const sitesData = await adminFunctions.getAllSites();
      setSites(sitesData);
    } catch (error) {
      toast({
        title: 'Error loading sites',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSite = async (updates) => {
    setLoading(true);
    try {
      await adminFunctions.updateSite(editingSite.id, updates);
      await loadSites();
      setEditingSite(null);
      toast({
        title: 'Site updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating site',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Are you sure you want to delete this site? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      await adminFunctions.deleteSite(siteId);
      await loadSites();
      toast({
        title: 'Site deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error deleting site',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      default:
        return 'yellow';
    }
  };

  const filteredSites = sites.filter(site =>
    site.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    site.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      <VStack spacing={4}>
        <Input
          placeholder="Search sites..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <Box overflowX="auto" w="100%">
          {loading ? (
            <Box textAlign="center" py={8}>
              <Spinner />
            </Box>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Description</Th>
                  <Th>Submitted By</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredSites.map((site) => (
                  <Tr key={site.id}>
                    <Td>{site.name}</Td>
                    <Td>
                      <Text noOfLines={2}>{site.description}</Text>
                    </Td>
                    <Td>{site.submittedBy}</Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(site.status)}>
                        {site.status || 'pending'}
                      </Badge>
                    </Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          aria-label='Options'
                          icon={<DotsThree size={20} />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          <MenuItem 
                            icon={<Pencil size={16} />}
                            onClick={() => setEditingSite(site)}
                          >
                            Edit
                          </MenuItem>
                          <MenuItem 
                            icon={<Trash size={16} />}
                            color="red.500"
                            onClick={() => handleDeleteSite(site.id)}
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </VStack>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingSite}
        onClose={() => setEditingSite(null)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Site</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {editingSite && (
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <Input
                    value={editingSite.name}
                    onChange={(e) => setEditingSite({...editingSite, name: e.target.value})}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={editingSite.description}
                    onChange={(e) => setEditingSite({...editingSite, description: e.target.value})}
                    minH="120px"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={editingSite.status || 'pending'}
                    onChange={(e) => setEditingSite({...editingSite, status: e.target.value})}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </Select>
                </FormControl>
                <HStack justify="flex-end" w="100%">
                  <Button variant="ghost" onClick={() => setEditingSite(null)}>Cancel</Button>
                  <Button colorScheme="blue" onClick={() => handleUpdateSite({
                    name: editingSite.name,
                    description: editingSite.description,
                    status: editingSite.status
                  })}>Save Changes</Button>
                </HStack>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
